import { useReducer } from 'react';
import { searchPageApi } from 'api/searchPageApi';
import type {
  IDFP,
  IGTM,
  IGetAdsRequestFilter,
  IGetAdsRequestRange,
  ISearchPageAds,
  ISearchPagePaging,
  ISearchPageSeoData,
  ISearchPageJatoAds,
  ISearchPageBreadcrumbs,
} from 'api/types/searchPageApiTypes';
import {
  mapFiltersRequest,
  mapGeoFilterRequest,
  mapLegacyAreaFilterValues,
  mapRangesRequest,
} from 'components/SearchPage/mappers/filters';
import type { MakeModelType } from 'components/SearchPage/hooks/useFiltersMakeModel';
import { CURRENCIES } from 'types';
import type { GeoFilter } from 'types';

export enum ACTION_TYPE {
  SET_STATE = 'SET_STATE',
  SET_LOADING = 'SET_LOADING',
  SET_ADS = 'SET_ADS',
  SET_ERROR = 'SET_ERROR',
  RESET = 'RESET',
}

export type AdsType = ISearchPageAds[] | [];
export type JatoAdsType = ISearchPageJatoAds[] | [];
export type SeoDataType = ISearchPageSeoData;
export type ErrorType = Error | null;

type SetLoadingType = {
  type: ACTION_TYPE.SET_LOADING;
  payload: boolean;
};

type SetAdsType = {
  type: ACTION_TYPE.SET_ADS;
  payload: {
    ads?: AdsType;
    spotlights?: AdsType;
    jatoAds?: JatoAdsType;
    seoData?: SeoDataType;
    dfp: IDFP;
    gtm: IGTM;
  };
};

type SetErrorType = {
  type: ACTION_TYPE.SET_ERROR;
  payload: ErrorType;
};

type StateType = {
  isLoading: boolean;
  error: ErrorType;
  ads?: AdsType;
  spotlights?: AdsType;
  jatoAds?: JatoAdsType;
  seoData?: SeoDataType;
  dfp: IDFP;
  gtm: IGTM;
};

type ActionType = SetLoadingType | SetAdsType | SetErrorType;

export const useAdsReducer = (state: StateType, action: ActionType) => {
  const { type, payload } = action;
  switch (type) {
    case ACTION_TYPE.SET_LOADING: {
      return {
        ...state,
        isLoading: payload as boolean,
      };
    }
    case ACTION_TYPE.SET_ADS: {
      const { ads, spotlights, jatoAds, seoData, dfp, gtm } =
        payload as StateType;
      return {
        isLoading: false,
        ads,
        spotlights,
        jatoAds,
        error: null,
        seoData,
        dfp,
        gtm,
      };
    }
    case ACTION_TYPE.SET_ERROR: {
      return {
        ...state,
        isLoading: false,
        error: payload as ErrorType,
      };
    }
    default:
      return state;
  }
};

export const mapMakeModelFilters = (makeModelFilters: MakeModelType) => {
  let response: { make: string; model: string; trim: string }[] = [];
  makeModelFilters.forEach((item) => {
    if (!item.makeValue || item.makeValue === 'All Makes') return;
    if (item.modelValue.length > 0) {
      item.modelValue.forEach((model) => {
        response.push({
          make: item.makeValue,
          model: model,
          trim: '',
        });
      });
    } else {
      response.push({
        make: item.makeValue,
        model: '',
        trim: '',
      });
    }
  });
  return response;
};

export type UseAdsType = StateType & {
  setLoading: (loading: boolean) => void;
  setAds: (
    ads: AdsType,
    spotlights: AdsType | undefined,
    jatoAds: JatoAdsType | undefined,
    seoData: SeoDataType | undefined,
    dfp: IDFP,
    gtm: IGTM,
  ) => void;
  setError: (error: ErrorType) => void;
  fetchAds: (
    filters: IGetAdsRequestFilter[],
    ranges: IGetAdsRequestRange[],
    makeModel: MakeModelType,
    page: number,
    pageSize: number,
    section: string,
    search: string,
    sort: string,
    userId: string | string[] | undefined,
    onStart: () => void,
    onSuccess: (
      breadcrumbs: ISearchPageBreadcrumbs[],
      recommendations?: ISearchPageAds[],
    ) => void,
    geoFilter?: GeoFilter,
  ) => void;
  combineAds: (
    ads?: AdsType,
    spotlightAds?: AdsType,
    jatoAds?: JatoAdsType,
  ) => ISearchPageAds[];
};

const useAds = (
  defaultAds: ISearchPageAds[] = [],
  defaultSpotlights: ISearchPageAds[] = [],
  defaultJatoAds: ISearchPageJatoAds[] = [],
  defaultSeoData: ISearchPageSeoData | undefined,
  defaultDFP: IDFP,
  defaultGTM: IGTM,
  setPaging: (paging: ISearchPagePaging) => void,
): UseAdsType => {
  const [state, dispatch] = useReducer(useAdsReducer, {
    isLoading: false,
    ads: defaultAds,
    spotlights: defaultSpotlights,
    jatoAds: defaultJatoAds,
    error: null,
    seoData: defaultSeoData,
    dfp: defaultDFP,
    gtm: defaultGTM,
  });

  const setLoading = (loading: boolean) =>
    dispatch({
      type: ACTION_TYPE.SET_LOADING,
      payload: loading,
    });

  const setAds = (
    ads: AdsType | undefined,
    spotlights: AdsType | undefined,
    jatoAds: JatoAdsType | undefined,
    seoData: SeoDataType | undefined,
    dfp: IDFP,
    gtm: IGTM,
  ) =>
    dispatch({
      type: ACTION_TYPE.SET_ADS,
      payload: {
        ads: ads as AdsType,
        spotlights: spotlights as AdsType,
        jatoAds: jatoAds as JatoAdsType,
        seoData: seoData as SeoDataType,
        dfp,
        gtm,
      },
    });

  const setError = (error: ErrorType) =>
    dispatch({
      type: ACTION_TYPE.SET_ERROR,
      payload: error,
    });

  const fetchAds = async (
    filters: IGetAdsRequestFilter[],
    ranges: IGetAdsRequestRange[],
    makeModel: MakeModelType,
    page: number,
    pageSize: number,
    section: string,
    search: string,
    sort: string,
    userId: string | string[] | undefined,
    onStart: () => void,
    onSuccess: (
      breadcrumbs: ISearchPageBreadcrumbs[],
      recommendations?: ISearchPageAds[],
    ) => void,
    geoFilter: GeoFilter,
  ) => {
    setLoading(true);
    onStart();
    const editedFilters = [...filters];

    if (userId) {
      Array.isArray(userId)
        ? editedFilters.push({
            name: 'userId',
            values: userId,
          })
        : editedFilters.push({
            name: 'userId',
            values: [userId],
          });
    }

    const mappedFilterValues = mapLegacyAreaFilterValues(
      editedFilters,
      geoFilter?.county,
      geoFilter?.countyTown,
    );

    try {
      const response = await searchPageApi.getAds(
        {
          geoFilter: mapGeoFilterRequest(geoFilter),
          filters: mapFiltersRequest(mappedFilterValues),
          ...(ranges.length && { ranges: mapRangesRequest(ranges) }),
          ...(makeModel.length && {
            makeModelFilters: mapMakeModelFilters(makeModel),
          }),
          paging: {
            pageSize,
            from: page * pageSize,
          },
          sections: [section],
          terms: search,
          sort,
        },
        { accept: 'application/json' },
        12000,
      );
      const {
        ads,
        spotlights,
        jatoAds,
        paging,
        seoData,
        dataTargetingProps: { dfp, gtm },
        breadcrumbs,
        recommendations,
      } = response.data;
      setAds(ads, spotlights, jatoAds, seoData, dfp, gtm);
      setPaging(paging);
      onSuccess(breadcrumbs, recommendations);
    } catch (err) {
      setError(err);
    }
  };

  const insertAtIndex = <T>(arr: T[], newItem: T, index: number) => {
    arr.splice(index, 0, newItem);
  };

  const mapJatoAd: (j: ISearchPageJatoAds) => ISearchPageAds = (jatoAd) => {
    return {
      currency: jatoAd.price[0] === '£' ? CURRENCIES.GBP : CURRENCIES.EUR,
      county: '',
      id: 0,
      header: jatoAd.header,
      userSaved: jatoAd.userSaved,
      photos: jatoAd.photos,
      price: jatoAd.price.slice(1),
      oldPriceView: {
        showOldPrice: false,
        oldPrice: null,
      },
      section: {
        displayName: 'Cars',
        name: 'cars',
      },
      wanted: false,
      age: '',
      friendlyUrl: jatoAd.friendlyUrl,
      deliveryAvailable: false,
      mediaCount: jatoAd.photos?.length,
      spotlight: false,
      spotlightable: false,
      herdwatchVerified: false,
      dealer: {
        franchiseType: 'Manufacturer',
        name: jatoAd.make,
        id: 0,
        address: '',
        showroomUrl: '',
        logo: {
          small: jatoAd?.adCardLogo,
        },
      },
      isJato: true,
      jatoPayload: {
        make: jatoAd.make,
        model: jatoAd.model,
        bodyType: jatoAd.bodyType,
      },
      highlight: 'BLUE_DARK',
      highlightText: 'NEW CAR',
      infoLabel: 'From',
      priceOnRequest: false,
    };
  };

  const mapSpotlightAds: (s?: AdsType) => ISearchPageAds[] = (spotlights) => {
    const mappedSpotlights: ISearchPageAds[] = [];
    spotlights &&
      spotlights.forEach((spotlight: ISearchPageAds) => {
        mappedSpotlights.push({
          ...spotlight,
          highlight: 'ORANGE',
          highlightText: 'SPOTLIGHT',
        });
      });
    return mappedSpotlights;
  };

  const combineAds = (
    ads?: AdsType,
    spotlightAds?: AdsType,
    jatoAds?: JatoAdsType,
  ) => {
    let combinedAds = [
      ...(mapSpotlightAds(spotlightAds) || []),
      ...(ads || []),
    ];
    if (jatoAds) {
      jatoAds.forEach((jatoAd, index) =>
        insertAtIndex<ISearchPageAds>(
          combinedAds,
          mapJatoAd(jatoAd),
          (index + 1) * 5 - 1,
        ),
      );
    }
    return combinedAds;
  };

  return {
    ...state,
    fetchAds,
    setLoading,
    setAds,
    setError,
    combineAds,
  };
};

export { useAds };
